import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { RefreshCw, Calendar as CalendarIcon, Clock, ArrowLeft, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { format, addDays, isBefore, startOfToday } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function RescheduleForm({ 
  token, 
  currentDate, 
  currentTime,
  onSuccess, 
  onCancel 
}) {
  const [step, setStep] = useState(1); // 1: date, 2: time, 3: confirm
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [reason, setReason] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch available dates on mount
  useEffect(() => {
    fetchAvailableDates();
  }, []);

  // Fetch slots when date selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableDates = async () => {
    try {
      setLoadingDates(true);
      const response = await fetch(`${BACKEND_URL}/api/v1/availability/dates?days=30`);
      if (response.ok) {
        const data = await response.json();
        setAvailableDates(data.dates || []);
      }
    } catch (err) {
      console.error('Error fetching dates:', err);
    } finally {
      setLoadingDates(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    try {
      setLoadingSlots(true);
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await fetch(`${BACKEND_URL}/api/v1/availability/slots?date=${dateStr}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots || []);
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch(`${BACKEND_URL}/api/v1/consultations/${token}/reschedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_date: format(selectedDate, 'yyyy-MM-dd'),
          new_time: selectedTime,
          reason: reason || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Consultation rescheduled successfully!');
        onSuccess?.(data);
      } else {
        throw new Error(data.detail || 'Failed to reschedule');
      }
    } catch (err) {
      console.error('Reschedule error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Check if date is available
  const isDateAvailable = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availableDates.some(d => d.date === dateStr && d.available_slots > 0);
  };

  const isDateDisabled = (date) => {
    return isBefore(date, startOfToday()) || !isDateAvailable(date);
  };

  return (
    <Card data-testid="reschedule-form">
      <CardHeader>
        <div className="flex items-center gap-3">
          {step > 1 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setStep(step - 1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Reschedule Consultation
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              {currentDate && currentTime && (
                <>Current: {currentDate} at {currentTime} ET</>
              )}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Progress Indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 mx-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Date Selection */}
        {step === 1 && (
          <div>
            <Label className="mb-3 block">Select a New Date</Label>
            {loadingDates ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={isDateDisabled}
                  className="rounded-md border"
                  data-testid="reschedule-calendar"
                />
              </div>
            )}
            <p className="text-sm text-gray-500 text-center mt-3">
              Available dates are highlighted. All times are Eastern Time (ET).
            </p>
          </div>
        )}

        {/* Step 2: Time Selection */}
        {step === 2 && (
          <div>
            <Label className="mb-3 block">
              Select a Time for {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </Label>
            {loadingSlots ? (
              <div className="grid grid-cols-3 gap-2">
                {[...Array(9)].map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : availableSlots.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No available slots for this date. Please select a different date.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {availableSlots.map((slot) => (
                  <Button
                    key={slot}
                    variant={selectedTime === slot ? 'default' : 'outline'}
                    onClick={() => handleTimeSelect(slot)}
                    className={selectedTime === slot ? 'bg-blue-600' : ''}
                    data-testid={`time-slot-${slot}`}
                  >
                    {slot}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div>
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800 mb-1">New Appointment Time:</p>
              <p className="text-lg font-semibold text-blue-900">
                {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-lg text-blue-800">{selectedTime} ET</p>
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Reason for rescheduling (optional)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Let us know why you need to reschedule..."
                className="h-24"
                data-testid="reschedule-reason"
              />
            </div>

            <Alert className="mb-4 border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Note:</strong> A new Zoom link will be generated for your rescheduled appointment.
              </AlertDescription>
            </Alert>

            <div className="flex gap-3">
              <Button data-testid="reschedule-form-button-1"
                variant="outline"
                onClick={onCancel}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={submitting}
                data-testid="confirm-reschedule-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rescheduling...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Confirm Reschedule</>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default RescheduleForm;
